import { Copy, ExternalLink, FileText, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { EmptyState } from "../data-display/EmptyState";
import { StatusBadge } from "../data-display/StatusBadge";
import { ListPageHeader } from "../list/ListPageHeader";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { DEFAULT_FORM_FIELDS, slugify, type LeadForm } from "./formModel";
import { FormFieldsEditor } from "./FormFieldsEditor";
import { FormSettingsPanel } from "./FormSettingsPanel";
import { FormAnalyticsPanel } from "./FormAnalyticsPanel";
import { FormSubmissionsPanel } from "./FormSubmissionsPanel";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Lead-capture forms builder (gravun.com Starter tier). Each form gets a
 * public /#/f/:slug page; submissions create contacts with source/UTM
 * attribution and can create deals, tasks, and sequence enrollments, * all server-side via submit_form.
 */
export const FormsPage = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending: creating }] = useCreate();
  const [newName, setNewName] = useState("");

  const { data: forms, isPending } = useGetList<LeadForm>("forms", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "ASC" },
  });

  const selected =
    forms?.find((f) => f.id === selectedId) ?? forms?.[0] ?? null;

  const createForm = () => {
    if (!newName.trim()) {
      notify("Name the form first", { type: "warning" });
      return;
    }
    const slug = `${slugify(newName)}-${Math.random().toString(36).slice(2, 6)}`;
    create(
      "forms",
      {
        data: {
          name: newName.trim(),
          slug,
          fields: DEFAULT_FORM_FIELDS,
          settings: { create_task: true },
          workspace_id: getActiveWorkspaceId(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: (record: LeadForm) => {
          notify("Form created");
          setNewName("");
          setSelectedId(record.id);
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not create", {
            type: "error",
          }),
      },
    );
  };

  if (isPending) return null;

  return (
    <>
      <ListPageHeader
        title="Forms"
        subtitle="Lead-capture forms with attribution, consent, and automatic follow-up"
        actions={
          <div className="flex items-center gap-2">
            <input
              className={cn(inputClass, "w-56")}
              placeholder="New form name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createForm()}
            />
            <Button size="sm" disabled={creating} onClick={createForm}>
              <Plus className="mr-1 size-3.5" />
              Create
            </Button>
          </div>
        }
      />

      {(forms ?? []).length === 0 ? (
        <Card className="py-16">
          <EmptyState
            icon={FileText}
            title="No forms yet"
            description="Create a form, share its public link, and every submission becomes an attributed lead with follow-up."
          />
        </Card>
      ) : (
        <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[280px_1fr]">
          <Card className="h-fit overflow-hidden py-0">
            {(forms ?? []).map((form) => (
              <button
                key={form.id}
                type="button"
                onClick={() => setSelectedId(form.id)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-3 text-left text-[13.5px] transition-colors last:border-0",
                  selected?.id === form.id
                    ? "bg-surface-muted font-medium text-foreground"
                    : "text-muted-foreground hover:bg-surface-muted/60",
                )}
              >
                <span className="truncate">{form.name}</span>
                <StatusBadge
                  label={form.active ? "Live" : "Off"}
                  tone={form.active ? "success" : "neutral"}
                />
              </button>
            ))}
          </Card>

          {selected ? <FormDetail form={selected} /> : null}
        </div>
      )}
    </>
  );
};

const FormDetail = ({ form }: { form: LeadForm }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const publicUrl = `${window.location.origin}/#/f/${form.slug}`;

  const toggleActive = () =>
    update(
      "forms",
      { id: form.id, data: { active: !form.active }, previousData: form },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    notify("Public link copied");
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold">{form.name}</h2>
            <p className="truncate text-[13px] text-muted-foreground">
              {publicUrl}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyLink}>
              <Copy className="mr-1 size-3.5" />
              Copy link
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1 size-3.5" />
                Open
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={toggleActive}>
              {form.active ? "Turn off" : "Turn on"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 [&>*]:min-w-0 xl:grid-cols-2">
        <FormFieldsEditor form={form} />
        <FormSettingsPanel form={form} />
      </div>

      <FormAnalyticsPanel form={form} />

      <FormSubmissionsPanel form={form} />
    </div>
  );
};

export const formsInputClass = inputClass;

export const DeleteFieldButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    variant="ghost"
    size="icon"
    className="size-7 text-muted-foreground hover:text-destructive"
    onClick={onClick}
  >
    <Trash2 className="size-3.5" />
  </Button>
);
